import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";
import { FailureSnackbar, SuccessSnackbar } from "./snackbar";

interface ICollaborationDetails {
  preferenceId: string;
  drawPencilMode: boolean;
  shapeSelection: boolean;
  selectedType: string;
  drawMode: boolean;
  property: {};
  save: boolean;
  templateData: any;
  shareAlertDialog: boolean;
  followModePreference: string;
  shareDialogPreference: boolean;
  assets?: IAssets[];
  updateLoader?: boolean;
}

interface IAssets {
  parentId: string;
  name: string;
  extension: string;
  category: string;
  type: string;
  location: string;
  id: string;
}

const initialState: ICollaborationDetails = {
  preferenceId: "",
  drawPencilMode: false,
  shapeSelection: false,
  selectedType: "rect",
  drawMode: false,
  property: {},
  save: false,
  templateData: [],
  shareAlertDialog: false,
  followModePreference: "",
  shareDialogPreference: false,
  assets: [],
  updateLoader: false,
};

const slice = createSlice({
  name: "collaboration",
  initialState,
  reducers: {
    setPreferenceId(state, action) {
      state.preferenceId = action.payload;
    },
    setDrawPencilMode(state, action) {
      state.drawPencilMode = action.payload;
    },
    setShapeSelection(state, action) {
      state.shapeSelection = action.payload;
    },
    setSelectedType(state, action) {
      state.selectedType = action.payload;
    },
    setDrawMode(state, action) {
      state.drawMode = action.payload;
    },
    setSave(state, action) {
      state.save = action.payload;
    },
    setTemplateData(state, action) {
      state.templateData = action.payload;
    },
    setShareAlertDialog(state, action) {
      state.shareAlertDialog = action.payload;
    },
    setFollowModePreference(state, action) {
      state.followModePreference = action.payload;
    },
    setShareDialogPreference(state, action) {
      state.shareDialogPreference = action.payload;
    },
    setAssets(state, action) {
      state.assets = action.payload;
    },
    setUpdateLoader(state, action) {
      state.updateLoader = action.payload;
    },
  },
});

export const {
  setPreferenceId,
  setDrawPencilMode,
  setShapeSelection,
  setSelectedType,
  setDrawMode,
  setTemplateData,
  setShareAlertDialog,
  setFollowModePreference,
  setShareDialogPreference
} = slice.actions;

export const reducer = slice.reducer;
export default slice;
const BASE_URL = `${process.env.REACT_APP_API_URL}/service-management`;

export const createDocument =
  (data, callback = null) =>
  async () => {
    try {
      const response = await axios.post(`${BASE_URL}/api/v1/document`, data);
      if (response?.data?.status) {
        const parts = response?.data?.message.split(" ");
        const id = parts[parts.length - 1];
        callback(id);
      }
    } catch (error) {
      throw new Error(error);
    }
  };

export const createPreferenceDocument = (data) => async (dispatch) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/v1/document`, data);
    if (response?.data?.status) {
      const parts = response?.data?.message.split(" ");
      const id = parts[parts.length - 1];
      dispatch(slice.actions.setPreferenceId(id));
    }
  } catch (error) {
    throw new Error(error);
  }
};

export const checkPreference = (PreferenceName) => async (dispatch) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/api/v1/document?name=${PreferenceName}&subType=custom`
    );
    if (response?.data?.status) {
      if (response?.data?.data.length === 0) {
        const tabData = {
          tab: {
            tabData: [],
            activeTab: "home",
            currentTabId: 0,
            leadingMode: false,
            readMode: false,
          },
        };
        const payload = {
          type: "preference",
          data: JSON.stringify(tabData),
          description: "",
          name: PreferenceName,
        };
        dispatch(createPreferenceDocument(payload));
      } else {
        dispatch(slice.actions.setPreferenceId(response?.data?.data[0]?.id));
      }
    }
  } catch (error) {
    throw new Error(error);
  }
};

export const getDocument =
  (id, callback = null) =>
  async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/v1/document?id=${id}&subType=custom`
      );
      if (response?.data?.status) {
        callback(response?.data?.data[0]?.data);
      }
    } catch (error) {
      throw new Error(error);
    }
  };

export const saveTemplate = (data, id) => async (dispatch) => {
  try {
    const response = await axios.patch(
      `${BASE_URL}/api/v1/document/${id}`,
      data
    );
    if (response?.data?.status) {
      dispatch(
        SuccessSnackbar({
          message: "Template created successfully !",
          open: true,
        })
      );
    } else
      dispatch(
        FailureSnackbar({
          message: response?.data?.message || "Failed to save template !",
          open: true,
        })
      );
  } catch (error) {
    dispatch(
      FailureSnackbar({
        message: error?.message || "Failed to save template !",
        open: true,
      })
    );
    throw new Error();
  }
};

export const getTemplate = (type, subType) => async (dispatch) => {
  try {
    let URL = "";
    if (type === "") {
      if (subType === "") {
        URL = `${BASE_URL}/api/v1/document`;
      } else {
        URL = `${BASE_URL}/api/v1/document/?subType=${subType}`;
      }
    } else {
      if (subType === "") {
        URL = `${BASE_URL}/api/v1/document/?type=${type}`;
      } else {
        URL = `${BASE_URL}/api/v1/document/?type=${type}&subType=${subType}`;
      }
    }
    const response = await axios.get(URL);
    if (response?.data?.status) {
      dispatch(slice.actions.setTemplateData(response?.data?.data));
    } else
      dispatch(
        FailureSnackbar({
          message: response?.data?.message || "Failed to save template !",
          open: true,
        })
      );
  } catch (error) {
    dispatch(
      FailureSnackbar({
        message: error?.message || "Failed to save template !",
        open: true,
      })
    );
    throw new Error();
  }
};

export const deleteTemplate = (id, data) => async (dispatch) => {
  try {
    const response = await axios.delete(`${BASE_URL}/api/v1/document/${id}`, {data});
    if (response?.data?.status){
      dispatch(
        SuccessSnackbar({
          message: "Template deleted successfully !",
          open: true,
        })
      );
    } else
      dispatch(
        FailureSnackbar({
          message: response?.data?.message || "Failed to delete template !",
          open: true,
        })
      );
  } catch (error) {
    dispatch(
      FailureSnackbar({
        message: error?.message || "Failed to delete template !",
        open: true,
      })
    );
    throw new Error();
  }
};

export const getImages = (category, parentId) => async (dispatch) => {
  try {
    dispatch(slice.actions.setUpdateLoader(true));
    const response = await axios.get(`${BASE_URL}/api/v1/library-assets?category=${category}&parentId=${parentId}`);
    if (response?.data?.status){
      dispatch(slice.actions.setAssets(response?.data?.data));
    } else
      dispatch(
        FailureSnackbar({
          message: response?.data?.message || "Failed to delete template !",
          open: true,
        })
      );
  } catch (error) {
    dispatch(
      FailureSnackbar({
        message: error?.message || "Failed to delete template !",
        open: true,
      })
    );
    throw new Error();
  } finally {
    dispatch(slice.actions.setUpdateLoader(false));
  }
};

export const acquireAsset = (id) => async () => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/service-management/api/v1/library-assets/${id}/acquire`
    );
    return response?.data?.data?.url;
  } catch {
    throw new Error();
  }
};

export const uploadImage = (data) => async (dispatch) => {
  try {
    const formData = new FormData();
    formData.append("parentId", "root");
    formData.append("asset", data);
    formData.append("name", data?.name);
    formData.append("category", "personal");
    formData.append("type", "file");
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/service-management/api/v1/library-assets`, formData);
    if(response?.data?.status) {
      dispatch(SuccessSnackbar({
        message: "Successfully uploaded Image",
        open: true
      }));
    } else if (!response?.data?.status) {
      dispatch(FailureSnackbar({
        message: "Failed to upload Image",
        open: true,
      }));
    }
    return;
  } catch {
    throw new Error();
  }
};
